// Variant comparison — live dual-camera 360 reveal (see
// docs/BEFORE-AFTER-SLIDER-WORKPLAN.md for the competitive context this
// closes: unlike the original frozen-screenshot version, drag-look stays
// live on both sides while the divider is open).
//
// Technique: two panorama spheres (current variant + comparison variant),
// each on its own dedicated pc.Layer, each rendered by its own camera whose
// `rect` places it on one side of the draggable divider (rect drives both
// the GL viewport AND — when the engine's internal `_scissorRectClear` flag
// is off, which it is outside XR — the scissor test too, so each camera's
// clear+draw calls are confined to its own half; the public `scissorRect`
// property looked like the right tool but is a dead end here — it's only
// consulted when that internal flag is set, which nothing in a normal render
// path ever does).
//
// A narrower `rect` alone isn't enough, though: PlayCanvas's default
// (`aspectRatioMode: ASPECT_AUTO`) recomputes aspect ratio from the rect's
// own width, which produces a *symmetric* FOV centered on each camera's own
// half — both cameras end up centered on the same forward direction with a
// narrower FOV, so the two halves don't continue into each other at the
// seam, they duplicate the center and skip everything outward of it. The
// fix is an **off-axis (oblique) frustum**: each camera's `calculateProjection`
// callback builds a `Mat4.setFrustum(...)` whose left/right bounds are a
// literal slice of the *full* camera's frustum — camA gets [-halfWidth,
// -halfWidth + 2*halfWidth*dividerFrac], camB gets the remainder — so what
// lands at the seam on each side is mathematically the same world direction.
// This is the standard technique for tiled/multi-viewport rendering (also
// how VR multi-view and CAVE projection systems split a single frustum).
//
// Both cameras copy the main camera entity's live transform every frame, so
// CameraControls' drag-look keeps working exactly as normal while comparing;
// the main camera's own render is simply disabled for the duration (its
// entity keeps moving, only the camera *component* stops drawing) so it
// doesn't also paint a third, unclipped copy underneath.
//
// Only panorama rooms get the live path — gsplat/glTF rooms fall back to the
// original frozen-snapshot behaviour (still useful, just not live), the same
// graceful-degradation philosophy already used when a splat/model URL fails
// to load.

import * as pc from 'playcanvas';
import type { Room, Variant } from '../app/types';
import { showToast } from './toast';
import { PanoramaSphere } from '../playcanvas/panoramaSphere';
import type { AssetLoader } from '../playcanvas/assetLoader';
import { rendererKindFor } from '../playcanvas/roomRenderer';

export interface VariantCompareDeps {
  app: pc.Application;
  canvas: HTMLCanvasElement;
  /** Main camera entity — read for live transform/fov; its camera *component* is disabled while comparing. */
  camera: pc.Entity;
  assets: AssetLoader;
  /** Swap to a variant + await its first render. Used only by the non-panorama fallback path. */
  swapAndRender: (room: Room, variant: Variant) => Promise<void>;
  /** Called right before the live/fallback overlay opens — hide competing UI (dollhouse, floor plan, hotspot labels). */
  onOpen?: () => void;
  /** Called after everything is torn down — restore whatever onOpen hid. */
  onClose?: () => void;
}

const OVERLAY_ID = 'variant-compare-overlay';
const LAYER_A_NAME = 'compare-a';
const LAYER_B_NAME = 'compare-b';

export class VariantCompare {
  private readonly deps: VariantCompareDeps;
  private overlay: HTMLElement | null = null;

  // Live dual-camera mode state.
  private layerA: pc.Layer | null = null;
  private layerB: pc.Layer | null = null;
  private sphereA: PanoramaSphere | null = null;
  private sphereB: PanoramaSphere | null = null;
  private camA: pc.Entity | null = null;
  private camB: pc.Entity | null = null;
  private dividerFrac = 0.5;
  private liveMode = false;

  constructor(deps: VariantCompareDeps) {
    this.deps = deps;
  }

  isOpen(): boolean {
    return this.overlay !== null;
  }

  /**
   * Open the compare overlay for `comparisonVariant` against `currentVariant`.
   * Live dual-sphere mode when both variants resolve to the panorama
   * renderer (the common case); frozen-snapshot fallback otherwise.
   */
  async open(room: Room, currentVariant: Variant, comparisonVariant: Variant): Promise<void> {
    if (this.isOpen()) return;
    if (currentVariant.id === comparisonVariant.id) {
      showToast('Selecione uma variante diferente para comparar', { tone: 'info' });
      return;
    }

    const bothPanorama =
      rendererKindFor(room, currentVariant) === 'panorama' && rendererKindFor(room, comparisonVariant) === 'panorama';

    if (bothPanorama) {
      await this.openLive(currentVariant, comparisonVariant);
    } else {
      await this.openSnapshotFallback(room, currentVariant, comparisonVariant);
    }
  }

  close(): void {
    if (!this.overlay) return;
    this.deps.onClose?.();
    if (this.liveMode) this.teardownLive();
    this.overlay.remove();
    this.overlay = null;
  }

  // ── Live dual-camera path ───────────────────────────────────────────

  private async openLive(currentVariant: Variant, comparisonVariant: Variant): Promise<void> {
    showToast('Preparando comparação ao vivo…', { tone: 'info' });
    let texA: pc.Texture;
    let texB: pc.Texture;
    try {
      const [a, b] = await Promise.all([
        this.deps.assets.loadPanorama(currentVariant.panoramaUrl, currentVariant.name),
        this.deps.assets.loadPanorama(comparisonVariant.panoramaUrl, comparisonVariant.name),
      ]);
      texA = a.texture;
      texB = b.texture;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[variant-compare] live texture load failed', err);
      showToast(`Falha na comparação: ${msg}`, { tone: 'error' });
      return;
    }

    this.deps.onOpen?.();
    this.liveMode = true;
    const { app, camera } = this.deps;

    this.layerA = this.getOrCreateLayer(LAYER_A_NAME);
    this.layerB = this.getOrCreateLayer(LAYER_B_NAME);

    this.sphereA = new PanoramaSphere(app, { layers: [this.layerA.id] });
    this.sphereA.setTexture(texA);
    this.sphereB = new PanoramaSphere(app, { layers: [this.layerB.id] });
    this.sphereB.setTexture(texB);

    const mainCam = camera.camera;
    if (mainCam) mainCam.enabled = false;

    // Convention matches the snapshot-fallback path below: A (current
    // variant) stays on the right, B (comparison variant) on the left — the
    // labels are positioned to match this and are shared by both modes.
    this.dividerFrac = 0.5;
    this.camA = this.createCompareCamera('compare-cam-a', this.layerA.id, 0, 'right');
    this.camB = this.createCompareCamera('compare-cam-b', this.layerB.id, 1, 'left');
    this.applyDividerRect();
    this.syncCameras();
    app.on('update', this.syncCameras);

    this.renderOverlay(currentVariant.name, comparisonVariant.name);
  }

  private getOrCreateLayer(name: string): pc.Layer {
    const existing = this.deps.app.scene.layers.getLayerByName(name);
    if (existing) return existing;
    const layer = new pc.Layer({ name });
    this.deps.app.scene.layers.push(layer);
    return layer;
  }

  /**
   * `side` picks which slice of the *full* camera's frustum this camera
   * covers — 'left' is always [0, dividerFrac], 'right' is always
   * [dividerFrac, 1]. calculateProjection reads `this.dividerFrac` fresh on
   * every call (the engine invokes it once per camera per frame), so drag
   * updates and canvas resizes are picked up automatically with no extra
   * invalidation bookkeeping.
   */
  private createCompareCamera(name: string, layerId: number, priority: number, side: 'left' | 'right'): pc.Entity {
    const mainCam = this.deps.camera.camera;
    const entity = new pc.Entity(name);
    entity.addComponent('camera', {
      nearClip: mainCam?.nearClip ?? 0.05,
      farClip: mainCam?.farClip ?? 1000,
      projection: pc.PROJECTION_PERSPECTIVE,
      clearColor: mainCam?.clearColor ?? new pc.Color(0, 0, 0, 1),
      layers: [layerId],
      priority,
    });
    const cam = entity.camera;
    if (cam) {
      cam.calculateProjection = (transformMatrix: pc.Mat4) => {
        const mc = this.deps.camera.camera;
        const fovY = mc?.fov ?? 72;
        const near = mc?.nearClip ?? 0.05;
        const far = mc?.farClip ?? 1000;
        const device = this.deps.app.graphicsDevice;
        const fullAspect = device.width / device.height;
        const halfHeight = near * Math.tan((fovY * Math.PI) / 360);
        const halfWidth = halfHeight * fullAspect;
        const xMin = side === 'left' ? 0 : this.dividerFrac;
        const xMax = side === 'left' ? this.dividerFrac : 1;
        const left = -halfWidth + 2 * halfWidth * xMin;
        const right = -halfWidth + 2 * halfWidth * xMax;
        transformMatrix.setFrustum(left, right, -halfHeight, halfHeight, near, far);
      };
    }
    this.deps.app.root.addChild(entity);
    return entity;
  }

  private readonly syncCameras = (): void => {
    if (!this.camA || !this.camB) return;
    const pos = this.deps.camera.getPosition();
    const rot = this.deps.camera.getRotation();
    this.camA.setPosition(pos);
    this.camA.setRotation(rot);
    this.camB.setPosition(pos);
    this.camB.setRotation(rot);
  };

  /** rect drives the actual screen placement (and, outside XR, the scissor
   *  test too — see the file-header note); calculateProjection above only
   *  controls the frustum *content*, not where on screen it lands. Must stay
   *  in lockstep with each camera's `side` — camA is always 'right' (current
   *  variant), camB always 'left' (comparison variant), matching the label
   *  convention shared with the snapshot-fallback path. */
  private applyDividerRect(): void {
    if (this.camA?.camera) this.camA.camera.rect = new pc.Vec4(this.dividerFrac, 0, 1 - this.dividerFrac, 1);
    if (this.camB?.camera) this.camB.camera.rect = new pc.Vec4(0, 0, this.dividerFrac, 1);
  }

  private teardownLive(): void {
    this.deps.app.off('update', this.syncCameras);
    this.camA?.destroy();
    this.camB?.destroy();
    this.sphereA?.destroy();
    this.sphereB?.destroy();
    this.camA = this.camB = null;
    this.sphereA = this.sphereB = null;
    const mainCam = this.deps.camera.camera;
    if (mainCam) mainCam.enabled = true;
    this.liveMode = false;
  }

  // ── Frozen-snapshot fallback (non-panorama rooms) ───────────────────

  private async openSnapshotFallback(room: Room, currentVariant: Variant, comparisonVariant: Variant): Promise<void> {
    showToast('Capturando comparação…', { tone: 'info' });
    try {
      const snapA = this.captureCanvas();
      await this.deps.swapAndRender(room, comparisonVariant);
      const snapB = this.captureCanvas();
      await this.deps.swapAndRender(room, currentVariant);
      this.deps.onOpen?.();
      this.renderOverlay(currentVariant.name, comparisonVariant.name, { snapA, snapB });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[variant-compare] snapshot capture failed', err);
      showToast(`Falha na comparação: ${msg}`, { tone: 'error' });
    }
  }

  private captureCanvas(): string {
    return this.deps.canvas.toDataURL('image/png');
  }

  // ── Shared overlay chrome (handle, labels, close button) ────────────

  private renderOverlay(labelA: string, labelB: string, snapshots?: { snapA: string; snapB: string }): void {
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Comparar variantes');
    // Live mode: transparent so the two live cameras underneath show through
    // and pointer events reach the canvas for drag-look; only the handle and
    // buttons opt back into pointer-events. Snapshot mode keeps the opaque
    // black background + static images it always had (nothing live to see
    // through, and drag-look isn't available on that path).
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9100;
      ${snapshots ? 'background: #000;' : 'background: transparent; pointer-events: none;'}
      user-select: none; -webkit-user-select: none;
    `;

    if (snapshots) {
      const imgB = document.createElement('img');
      imgB.src = snapshots.snapB;
      imgB.alt = labelB;
      imgB.style.cssText = `position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; pointer-events: none;`;
      overlay.appendChild(imgB);

      const imgA = document.createElement('img');
      imgA.src = snapshots.snapA;
      imgA.alt = labelA;
      imgA.style.cssText = `position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; pointer-events: none; clip-path: inset(0 0 0 50%);`;
      overlay.appendChild(imgA);
    }

    // The visible seam line is thin (3px), but the *grabbable* hit area is a
    // much wider invisible strip centered on it — a 3px pointerdown target is
    // nearly impossible to land on with a mouse, let alone a finger. Once
    // grabbed, setPointerCapture keeps drag events routed to this element
    // regardless of where the pointer wanders, so only the initial hit area
    // needed widening.
    const handle = document.createElement('div');
    handle.style.cssText = `
      position: absolute; top: 0; bottom: 0; left: 50%;
      width: 64px; transform: translateX(-50%);
      cursor: ew-resize;
      touch-action: none; pointer-events: auto;
    `;
    const line = document.createElement('div');
    line.style.cssText = `
      position: absolute; top: 0; bottom: 0; left: 50%;
      width: 3px; background: rgba(255,255,255,0.92);
      box-shadow: 0 0 12px rgba(0,0,0,0.6);
      transform: translateX(-50%);
      pointer-events: none;
    `;
    handle.appendChild(line);
    const knob = document.createElement('div');
    knob.style.cssText = `
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 48px; height: 48px; border-radius: 50%;
      background: rgba(255,255,255,0.95);
      box-shadow: 0 4px 14px rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      color: #0a1620; font-weight: 700; font-size: 14px; font-family: system-ui, sans-serif;
      pointer-events: none;
    `;
    knob.textContent = '◀ ▶';
    handle.appendChild(knob);
    overlay.appendChild(handle);

    const labelARect = document.createElement('div');
    labelARect.style.cssText = `position: absolute; top: 24px; right: 24px; padding: 8px 14px; background: rgba(0,0,0,0.72); color: #ffffff; border-radius: 6px; font-family: system-ui, sans-serif; font-size: 13px; font-weight: 600; box-shadow: 0 4px 12px rgba(0,0,0,0.3); pointer-events: none;`;
    labelARect.textContent = labelA;
    overlay.appendChild(labelARect);
    const labelBRect = document.createElement('div');
    labelBRect.style.cssText = `position: absolute; top: 24px; left: 24px; padding: 8px 14px; background: rgba(0,0,0,0.72); color: #ffffff; border-radius: 6px; font-family: system-ui, sans-serif; font-size: 13px; font-weight: 600; box-shadow: 0 4px 12px rgba(0,0,0,0.3); pointer-events: none;`;
    labelBRect.textContent = labelB;
    overlay.appendChild(labelBRect);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = '✕ Sair da comparação';
    closeBtn.style.cssText = `
      position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);
      padding: 10px 20px; font-size: 14px; font-weight: 600;
      border: none; border-radius: 8px;
      background: rgba(0,0,0,0.78); color: #ffffff;
      cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,0.4);
      font-family: system-ui, sans-serif;
      pointer-events: auto;
    `;
    closeBtn.addEventListener('click', () => this.close());
    overlay.appendChild(closeBtn);

    let dragging = false;
    const setPosition = (clientX: number): void => {
      const rect = overlay.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const pct = (x / rect.width) * 100;
      handle.style.left = `${pct}%`;
      if (snapshots) {
        const imgA = overlay.querySelector('img[style*="clip-path"]') as HTMLImageElement | null;
        if (imgA) imgA.style.clipPath = `inset(0 0 0 ${pct}%)`;
      } else {
        this.dividerFrac = x / rect.width;
        this.applyDividerRect();
      }
    };
    const onDown = (e: PointerEvent): void => {
      dragging = true;
      handle.setPointerCapture?.(e.pointerId);
      setPosition(e.clientX);
    };
    const onMove = (e: PointerEvent): void => {
      if (!dragging) return;
      setPosition(e.clientX);
    };
    const onUp = (e: PointerEvent): void => {
      dragging = false;
      handle.releasePointerCapture?.(e.pointerId);
    };
    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        this.close();
        window.removeEventListener('keydown', onKey);
      }
    };
    window.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    this.overlay = overlay;
  }
}
